import { useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, AlertCircle, CheckCircle, X } from "lucide-react";
import { toast } from "sonner";
import { useBulkUpload } from "./bulk-upload.hooks";
import { planBulkUpload } from "./bulk-upload.api";
import {
  parseVendorAppointmentBulkTSV,
  type AppointmentBulkUploadParseResult,
  type VendorAppointmentBulkUploadRow,
} from "@/app/shared/appointments/bulk-upload-parser";
import { useBulkUploadPlan } from "@/app/shared/appointments/bulk-upload.hooks";
import BulkUploadPlanSummary, { CappedList } from "@/app/shared/appointments/components/BulkUploadPlanSummary";

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
}

type ValidationResult = AppointmentBulkUploadParseResult<VendorAppointmentBulkUploadRow>;

export default function BulkUploadModal({ isOpen, onClose, onUploadComplete }: BulkUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadAppointments, isUploading, uploadProgress, uploadResult, resetUpload } = useBulkUpload();
  const { plan, isPlanning, planError, resetPlan, runPlanCheck, importCount } = useBulkUploadPlan(planBulkUpload);

  const validateTSV = useCallback((content: string): ValidationResult => parseVendorAppointmentBulkTSV(content), []);

  const handleFileChange = useCallback(
    (selectedFile: File | null) => {
      resetPlan();

      if (!selectedFile) {
        setFile(null);
        setValidationResult(null);
        return;
      }

      // Check file extension
      if (!selectedFile.name.toLowerCase().endsWith(".tsv") && !selectedFile.name.toLowerCase().endsWith(".txt")) {
        setValidationResult({
          isValid: false,
          errors: ["File must be a TSV (.tsv) or text (.txt) file"],
          data: [],
        });
        return;
      }

      setFile(selectedFile);

      // Read and validate file
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const result = validateTSV(content);
        setValidationResult(result);
        if (result.isValid && result.data.length > 0) {
          void runPlanCheck(result.data);
        }
      };
      reader.readAsText(selectedFile);
    },
    [validateTSV, resetPlan, runPlanCheck]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileChange(files[0]);
      }
    },
    [handleFileChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleUpload = async () => {
    if (!plan) return;

    try {
      // Unit creation is a PMC-only permission, so vendors always skip missing-unit rows.
      const result = await uploadAppointments(plan, { createMissingUnits: false });

      if (result.success) {
        toast.success("Bulk Upload Complete", {
          description: result.message,
        });
        onUploadComplete?.();
        handleClose();
      } else {
        toast.error("Upload Failed", {
          description: result.message,
        });
        // Re-check against the database so retrying doesn't re-import the rows that succeeded.
        if (validationResult?.data.length) {
          void runPlanCheck(validationResult.data);
        }
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Upload Failed", {
        description: error instanceof Error ? error.message : "An unexpected error occurred",
      });
    }
  };

  const handleClose = () => {
    setFile(null);
    setValidationResult(null);
    resetPlan();
    resetUpload();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };

  const downloadTemplate = () => {
    const template = [
      "property\tproperty_unit\tservice\tsub_service\tdate\twork_notes",
      "Sunset Apartments\t101\tCleaning\tDeep Clean\t2024-01-15\tRegular cleaning service",
      "Sunset Apartments\t102\tMaintenance\tHVAC\t2024-01-16\tHVAC maintenance",
    ].join("\n");

    const blob = new Blob([template], { type: "text/tab-separated-values" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vendor_appointments_template.tsv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Upload Appointments</DialogTitle>
          <DialogDescription>
            Upload multiple appointments using a TSV (Tab-Separated Values) file. Required columns: Property, Property
            Unit, Service, Sub Service, Date, Work Notes. Note: Vendor is automatically set to your organization.
            <Button variant="link" className="p-0 h-auto" onClick={downloadTemplate}>
              Download template
            </Button>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload Area */}
          <div className="space-y-2">
            <Label htmlFor="file-upload">Select TSV File</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={handleUploadAreaClick}
            >
              <div className="flex flex-col items-center space-y-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium">Click to upload</span> or drag and drop
                </div>
                <div className="text-xs text-muted-foreground">TSV files only (Max 10MB)</div>
              </div>
              <Input
                ref={fileInputRef}
                id="file-upload"
                type="file"
                accept=".tsv,.txt"
                className="hidden"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          {/* File Info */}
          {file && (
            <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
              <FileText className="h-4 w-4" />
              <span className="text-sm font-medium">{file.name}</span>
              <span className="text-sm text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
              <Button variant="ghost" size="sm" className="ml-auto h-6 w-6 p-0" onClick={() => handleFileChange(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Validation Results */}
          {validationResult && (
            <div className="space-y-2">
              {validationResult.isValid ? (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    File is valid! Found {validationResult.data.length} appointment
                    {validationResult.data.length !== 1 ? "s" : ""} to upload.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <div>
                        Found {validationResult.errors.length} error{validationResult.errors.length !== 1 ? "s" : ""}:
                      </div>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        {validationResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Pre-import check: resolves references, duplicates, and missing units */}
          {validationResult?.isValid && (
            <BulkUploadPlanSummary
              plan={plan}
              isPlanning={isPlanning}
              planError={planError}
              allowCreateUnits={false}
              createMissingUnits={false}
              onCreateMissingUnitsChange={() => undefined}
            />
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Uploading appointments...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <Alert className={uploadResult.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
              {uploadResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={uploadResult.success ? "text-green-800" : "text-red-800"}>
                <div className="space-y-1">
                  <div>{uploadResult.message}</div>
                  {uploadResult.errors && uploadResult.errors.length > 0 && <CappedList items={uploadResult.errors} />}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={importCount === 0 || isUploading || isPlanning}>
            {isUploading
              ? "Uploading..."
              : importCount > 0
                ? `Import ${importCount} Appointment${importCount !== 1 ? "s" : ""}`
                : "Upload Appointments"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
